import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Check, Trash2, BellRing } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";

export interface Notification {
  id: string;
  user_id: string;
  title: string;
  message: string;
  is_read: boolean;
  link: string | null;
  created_at: string;
}

export const NotificationBell = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('notifications' as any)
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error("Error fetching notifications:", error);
      } else {
        setNotifications((data as any[]) || []);
      }
    } catch (err) {
      console.error(err);
    }
  }, [user]);

  useEffect(() => {
    fetchNotifications();

    if (!user) return;

    // Realtime Subscription
    const channel = supabase
      .channel('public:notifications')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        (payload) => {
          setNotifications((prev) => [payload.new as Notification, ...prev]);
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        (payload) => {
          setNotifications((prev) =>
            prev.map((n) => (n.id === payload.new.id ? (payload.new as Notification) : n))
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchNotifications]);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const markAsRead = async (id: string) => {
    try {
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      await supabase.from('notifications' as any).update({ is_read: true }).eq('id', id);
    } catch (err) {
      console.error(err);
    }
  };

  const markAllAsRead = async () => {
    try {
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      await supabase.from('notifications' as any).update({ is_read: true }).eq('user_id', user?.id).eq('is_read', false);
    } catch (err) {
      console.error(err);
    }
  };

  const deleteNotification = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setNotifications(prev => prev.filter(n => n.id !== id));
      await supabase.from('notifications' as any).delete().eq('id', id);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative hover:bg-slate-100 rounded-full transition-colors">
          <Bell className={`h-5 w-5 transition-colors ${unreadCount > 0 ? "text-red-500 fill-red-50" : "text-slate-600"}`} />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-600 border border-white"></span>
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-80 sm:w-96 p-0 border rounded-xl overflow-hidden shadow-xl bg-white">
        
        <div className="flex items-center justify-between p-4 bg-slate-50 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-slate-900">Notifications</h3>
            {unreadCount > 0 && (
              <Badge className="bg-red-500 hover:bg-red-600 border-0 px-1.5 font-medium shadow-none">
                {unreadCount} New
              </Badge>
            )}
          </div>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={markAllAsRead} 
              className="h-8 text-[11px] font-medium text-slate-500 hover:text-slate-900 transition-colors"
            >
              <Check className="h-3.5 w-3.5 mr-1" /> Mark all read
            </Button>
          )}
        </div>
        
        <ScrollArea className="max-h-[420px]">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-slate-500">
              <BellRing className="h-10 w-10 text-slate-200 mb-3" />
              <p className="text-sm font-medium">You're all caught up!</p>
            </div>
          ) : (
            <div className="flex flex-col pb-2">
              {notifications.map((notif) => (
                <DropdownMenuItem 
                  key={notif.id} 
                  className={`flex flex-col items-start gap-1.5 p-4 cursor-pointer border-b border-slate-100 last:border-0 rounded-none transition-colors outline-none ${
                    !notif.is_read ? 'bg-red-50/30 hover:bg-red-50/50' : 'bg-white hover:bg-slate-50'
                  }`}
                  onClick={() => !notif.is_read && markAsRead(notif.id)}
                >
                  <div className="flex w-full justify-between items-start gap-3">
                    <h4 className={`text-sm tracking-tight ${!notif.is_read ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>
                      {notif.title}
                    </h4>
                    <span className="text-[10px] font-medium text-slate-400 pt-0.5 whitespace-nowrap">
                      {new Date(notif.created_at).toLocaleDateString()}
                    </span>
                  </div>
                    
                  <p className={`text-[13px] leading-relaxed line-clamp-2 ${!notif.is_read ? 'text-slate-800' : 'text-slate-500'}`}>
                    {notif.message}
                  </p>
                    
                  <div className="flex w-full justify-between items-center mt-2 pt-2 border-t border-slate-100/60">
                    <span className="text-[10px] font-medium text-slate-400">
                      {new Date(notif.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <button 
                      onClick={(e) => deleteNotification(notif.id, e)}
                      className="text-slate-300 hover:text-red-500 p-1 rounded transition-colors"
                      title="Remove notification"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </DropdownMenuItem>
              ))}
            </div>
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
